import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiSearch, FiShoppingBag, FiEye, FiEdit2 } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import DateRangeFilter from "../../../../Common/DateFilter";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { getallProject } from "../../../../Redux/actions/getprojects";
import { filterStoreProjects } from "../../projectFilters";
import { useSession } from "../../../../context/SessionContext";

import {
  getMaterialRequisitions,
  saveMaterialRequisition,
  updateRequisitionStatus,
  deleteMaterialRequisition,
  cancelRequisitionLine,
} from "./materialRequisitionsService";
import RequisitionDetailModal from "./RequisitionDetailModal";
import EditRequisitionModal from "./EditRequisitionModal";
import BulkPoModal from "./BulkPoModal";

const MaterialRequisitions = () => {
  const dispatch = useDispatch();
  const { session } = useSession();
  const gridRef = useRef(null);

  // Redux data for projects
  const rolesData = useSelector((state) => state.GetAllRole);
  const projectData = useSelector((state) => state.getProject);

  const availableProjects = useMemo(() => {
    const rawList = rolesData?.data?.Projects || projectData?.data || [];
    return filterStoreProjects(rawList);
  }, [rolesData?.data?.Projects, projectData?.data]);

  // Main data state
  const [requisitions, setRequisitions] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Column visibility state matching other pages
  const [columnVisibility, setColumnVisibility] = useState({});

  // Filter toolbar state matching Stock Report
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDates, setFilterDates] = useState(null);

  // Modals state
  const [viewingRequisition, setViewingRequisition] = useState(null);
  const [editingRequisition, setEditingRequisition] = useState(null);
  const [deletingRequisition, setDeletingRequisition] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showBulkPoModal, setShowBulkPoModal] = useState(false);
  const [poTargetRequisitions, setPoTargetRequisitions] = useState([]);

  // Fetch initial data & projects
  useEffect(() => {
    dispatch(getallProject());
    getMaterialRequisitions(session?.LoginId)
      .then(setRequisitions)
      .catch((e) => toast.error(e.message));
  }, [dispatch, session?.LoginId]);

  // Live filtered rows
  const filteredRows = useMemo(() => {
    return requisitions.filter((row) => {
      // Project filter
      if (
        selectedProjectId &&
        row.project &&
        row.project.toLowerCase() !== selectedProjectId.toLowerCase()
      ) {
        return false;
      }

      // Priority filter
      if (
        selectedPriority &&
        selectedPriority !== "All Priorities" &&
        row.priority.toLowerCase() !== selectedPriority.toLowerCase()
      ) {
        return false;
      }

      // Status filter
      if (selectedStatus && selectedStatus !== "All Statuses") {
        if (selectedStatus === "All Pending") {
          if (!row.approvalStatus.toLowerCase().includes("pending")) {
            return false;
          }
        } else if (row.approvalStatus.toLowerCase() !== selectedStatus.toLowerCase()) {
          return false;
        }
      }

      // Date Range filter
      if (filterDates?.from && filterDates?.to && row.dateRaised) {
        const parts = row.dateRaised.split("/");
        if (parts.length === 3) {
          const rowDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          const fromDate = new Date(filterDates.from);
          const toDate = new Date(filterDates.to);
          if (rowDate < fromDate || rowDate > toDate) {
            return false;
          }
        }
      }

      // Search Query
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const inReq = (row.reqNo || "").toLowerCase().includes(q);
        const inItems = (row.itemsSummary || "").toLowerCase().includes(q);
        const inReqBy = (row.requestedBy || "").toLowerCase().includes(q);
        const inProj = (row.project || "").toLowerCase().includes(q);
        if (!inReq && !inItems && !inReqBy && !inProj) {
          return false;
        }
      }

      return true;
    });
  }, [requisitions, selectedProjectId, selectedStatus, selectedPriority, filterDates, searchTerm]);

  // Handle Action functions
  const handleDecideRequisition = useCallback(async (id, status, comments) => {
    try {
      setRequisitions(await updateRequisitionStatus(id, status, comments));
      toast.success(`Requisition ${status.toLowerCase()} successfully!`);
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  const handleCancelLine = useCallback(async (id, lineId, quantity, reason) => {
    try {
      const updated = await cancelRequisitionLine(id, lineId, quantity, reason);
      setRequisitions(updated);
      setViewingRequisition((v) => (v ? updated.find((r) => r.id === v.id) || null : v));
      toast.success("Line quantity cancelled");
    } catch (e) {
      toast.error(e.message);
    }
  }, []);

  const handleConfirmDelete = async () => {
    if (!deletingRequisition) {
      return;
    }
    if (!cancelReason.trim()) {
      toast.error("Reason is required");
      return;
    }
    setIsDeleting(true);
    try {
      const updated = await deleteMaterialRequisition(deletingRequisition.id, cancelReason.trim());
      setRequisitions(updated);
      setSelectedRows((prev) => {
        return prev.filter((r) => r.id !== deletingRequisition.id);
      });
      toast.success(`Requisition "${deletingRequisition.reqNo}" cancelled successfully`);
    } catch (err) {
      toast.error(err.message || "Failed to cancel requisition");
    } finally {
      setIsDeleting(false);
      setDeletingRequisition(null);
      setCancelReason("");
    }
  };

  // No try/catch: the edit modal shows the error.
  const handleSaveEdit = async (updatedReq) => {
    setRequisitions(await saveMaterialRequisition(updatedReq));
  };

  const handleSingleCreatePO = useCallback((req) => {
    setPoTargetRequisitions([req]);
    setShowBulkPoModal(true);
  }, []);

  const handleBulkCreatePO = useCallback(() => {
    if (selectedRows.length === 0) {
      return;
    }
    setPoTargetRequisitions(selectedRows);
    setShowBulkPoModal(true);
  }, [selectedRows]);

  // AG Grid Selection Change
  const onSelectionChanged = useCallback(() => {
    if (gridRef.current?.api) {
      const selected = gridRef.current.api.getSelectedRows();
      setSelectedRows(selected || []);
    }
  }, []);

  // Column Definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        colId: "checkbox",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 45,
        minWidth: 45,
        maxWidth: 50,
        pinned: "left",
        sortable: false,
        filter: false,
        resizable: false,
      },
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        filter: false,
        sortable: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Req #",
        field: "reqNo",
        minWidth: 145,
        flex: 1.1,
        cellRenderer: (params) => {
          return (
            <span
              style={{ fontWeight: 600, color: "#056363", cursor: "pointer" }}
              onClick={() => {
                setViewingRequisition(params.data);
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        headerName: "Project / Site",
        field: "project",
        minWidth: 140,
        flex: 1.2,
      },
      {
        headerName: "Requested By",
        field: "requestedBy",
        minWidth: 140,
        flex: 1.2,
      },
      {
        headerName: "Date Raised",
        field: "dateRaised",
        minWidth: 110,
        flex: 1,
      },
      {
        headerName: "Required By",
        field: "requiredBy",
        minWidth: 110,
        flex: 1,
      },
      {
        headerName: "Items / Materials Requested",
        field: "itemsSummary",
        minWidth: 230,
        flex: 1.8,
        cellRenderer: (params) => {
          return <span style={{ whiteSpace: "pre-line", lineHeight: "1.3" }}>{params.value}</span>;
        },
      },
      {
        headerName: "Est. Cost (PKR)",
        field: "estCost",
        minWidth: 130,
        flex: 1,
        valueFormatter: (p) => {
          return Number(p.value || 0).toLocaleString();
        },
        cellStyle: { fontWeight: 700, color: "#0f172a" },
      },
      {
        headerName: "Priority",
        field: "priority",
        minWidth: 130,
        flex: 1,
        cellRenderer: (params) => {
          const priority = params.value || "Medium";
          let type = "warning";
          if (priority === "High / Urgent") {
            type = "error";
          } else if (priority === "Low") {
            type = "info";
          }
          return <StatusChip status={priority} type={type} />;
        },
      },
      {
        headerName: "Approval Status",
        field: "approvalStatus",
        minWidth: 140,
        flex: 1,
        cellRenderer: (params) => {
          const status = params.value || "Pending Approval";
          return <StatusChip status={status} type={statusChipType(status)} />;
        },
      },
      {
        headerName: "Actions",
        colId: "actions",
        minWidth: 120,
        width: 120,
        sortable: false,
        filter: false,
        cellRenderer: (params) => {
          const row = params.data;
          if (!row) {
            return null;
          }

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: "12px",
              }}
            >
              <FiEye
                title="View Details"
                size="1.15em"
                style={{ cursor: "pointer", color: "#0284c7" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingRequisition(row);
                }}
              />
              <FiEdit2
                title="Edit Requisition"
                size="1.1em"
                style={{ cursor: "pointer", color: "#4e545a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!row.canEdit) {
                    toast.info("This requisition can no longer be changed.");
                    return;
                  }
                  setEditingRequisition(row);
                }}
              />
              <MdDeleteForever
                title="Cancel Requisition"
                size="1.2em"
                className="cur_sor"
                style={{ cursor: "pointer", color: "#c0392b" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!row.canCancel) {
                    toast.info("This requisition can no longer be cancelled.");
                    return;
                  }
                  setDeletingRequisition(row);
                }}
              />
            </div>
          );
        },
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      tooltipValueGetter: (p) => p.value,
      valueFormatter: (p) => {
        return p.value === null || p.value === undefined || p.value === "" ? "N/A" : p.value;
      },
      minWidth: 100,
    }),
    [],
  );

  // Column visibility handling matching StockReport
  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => {
        return { ...prev, [colId]: !isCurrentlyVisible };
      });
    },
    [columnVisibility],
  );

  return (
    <>
      <div
        className="d-flex flex-wrap align-items-center justify-content-end pt-3 app_font w-100"
        style={{ gap: "12px", width: "100%" }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            minHeight: "30px",
            maxWidth: "100%",
            marginRight: "auto",
            padding: "4px 12px",
            color: "var(--btn-comp-text, #0b6462)",
            fontSize: "var(--font-md)",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
          title="Purchase Requisitions"
        >
          Purchase Requisitions
        </div>
      </div>

      {/* Top Filters Toolbar matching Stock Report theme */}
      <div className="stock-filters-card mt-2 mb-1">
        <div className="stock-filters-grid">
          {/* Project / Store */}
          <div className="filter-group">
            <label className="filter-label">Project / Store</label>
            <select
              className="filter-select"
              value={selectedProjectId}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
              }}
            >
              <option value="">All Projects</option>
              {availableProjects.map((p) => (
                <option key={p.ProjectId} value={p.ProjectName}>
                  {p.ProjectName}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <DateRangeFilter
              initialFrom={filterDates?.from || ""}
              initialTo={filterDates?.to || ""}
              onChange={(val) => {
                setFilterDates(val || null);
              }}
            />
          </div>

          {/* Status */}
          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-select"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
              }}
            >
              <option value="">All Statuses</option>
              <option value="All Pending">All Pending</option>
              <option value="Pending Approval">Pending Approval</option>
              <option value="Approved">Approved</option>
              <option value="Partially Ordered">Partially Ordered</option>
              <option value="Ordered">Ordered</option>
              <option value="Closed">Closed</option>
              <option value="Rejected">Rejected</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          {/* Priority */}
          <div className="filter-group">
            <label className="filter-label">Priority</label>
            <select
              className="filter-select"
              value={selectedPriority}
              onChange={(e) => {
                setSelectedPriority(e.target.value);
              }}
            >
              <option value="">All Priorities</option>
              <option value="High / Urgent">High / Urgent</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          {/* Search */}
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <FiSearch
                style={{
                  position: "absolute",
                  left: "10px",
                  color: "#94a3b8",
                  fontSize: "14px",
                }}
              />
              <input
                type="text"
                className="filter-input"
                style={{ paddingLeft: "32px" }}
                placeholder="Search by Req # or Material..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              />
            </div>
          </div>

          {/* Column Visibility */}
          <div className="d-flex align-items-end pb-1" style={{ flexShrink: 0 }}>
            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* Standard AG Grid Table (Matches StockReport and other ERP pages) */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div className="ag-theme-alpine vouchers_table_height3 mt-1" style={{ width: "100%" }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredRows || []}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              quickFilterText={searchTerm}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectionChanged}
              rowStyle={{ cursor: "pointer" }}
              onRowClicked={(e) => {
                if (!e.event.target.closest("button, input, svg")) {
                  setViewingRequisition(e.data);
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Table Footer Actions */}
      <div className="row mt-2">
        <div className="col-12 pr-0 pl-0 d-flex justify-content-end align-items-center">
          <button
            type="button"
            className="btn_primary"
            disabled={selectedRows.length === 0}
            onClick={handleBulkCreatePO}
            style={{
              height: "36px",
              padding: "0 20px",
              fontSize: "13px",
              fontWeight: 600,
              opacity: selectedRows.length === 0 ? 0.5 : 1,
              cursor: selectedRows.length === 0 ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiShoppingBag /> Bulk Create PO for selected{" "}
            {selectedRows.length > 0 ? `(${selectedRows.length})` : ""}
          </button>
        </div>
      </div>

      {/* View Details Modal */}
      {viewingRequisition && (
        <RequisitionDetailModal
          open={Boolean(viewingRequisition)}
          onClose={() => {
            setViewingRequisition(null);
          }}
          requisition={viewingRequisition}
          onDecide={handleDecideRequisition}
          onCancelLine={handleCancelLine}
          onCreatePO={handleSingleCreatePO}
        />
      )}

      {/* Edit Requisition Modal */}
      {editingRequisition && (
        <EditRequisitionModal
          open={Boolean(editingRequisition)}
          onClose={() => {
            setEditingRequisition(null);
          }}
          requisition={editingRequisition}
          onSave={handleSaveEdit}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={Boolean(deletingRequisition)}
        onClose={() => {
          setDeletingRequisition(null);
          setCancelReason("");
        }}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        message={`Are you sure you want to cancel Requisition "${deletingRequisition?.reqNo}"?`}
        heading="Cancel Requisition"
        actionText="Confirm"
      >
        <label className="filter-label-text" style={{ display: "block", marginBottom: "4px" }}>
          Reason *
        </label>
        <input
          type="text"
          className="filter-control-input"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </DeleteConfirmModal>

      {/* Bulk Purchase Order Modal */}
      {showBulkPoModal && (
        <BulkPoModal
          open={showBulkPoModal}
          onClose={() => {
            setShowBulkPoModal(false);
          }}
          selectedRequisitions={poTargetRequisitions}
        />
      )}
    </>
  );
};

export default MaterialRequisitions;
